import React, { memo } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Box, Typography, Chip, CircularProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  useTheme, useMediaQuery,
} from '@mui/material';
import { History as HistoryIcon } from '@mui/icons-material';
import { AuditLogFromCell, AuditLogToCell, AuditLogClientLinkCell, AuditLogOrderLinkCell } from '../AuditLogTableCells';
import { formatDateTime } from '../../utils/dateFormat';

const ACTION_LABELS = {
  order_created: 'Order Created',
  order_status_change: 'Order Status',
  item_status_change: 'Status Change',
  item_returned_to_inventory: 'Returned to Inventory',
  item_price_updated: 'Price/Weight Updated',
  diamond_status_change: 'Diamond Status',
  lot_available_carat_updated: 'Lot Available Carat',
  payment_created: 'Payment Recorded',
  payment_cancelled: 'Payment Cancelled',
};

function formatAuditAction(action) {
  return ACTION_LABELS[action] || action;
}

function getAuditActionColor(action) {
  switch (action) {
    case 'order_created': return 'primary';
    case 'order_status_change': return 'warning';
    case 'item_status_change': return 'info';
    case 'item_returned_to_inventory': return 'success';
    case 'item_price_updated': return 'info';
    default: return 'default';
  }
}

function getAuditStatusLabel(status) {
  if (!status) return '';
  const labels = {
    requested: 'Requested', approved: 'Approved', ready: 'Ready',
    canceled: 'Cancelled', expired: 'Expired', memo: 'Memo',
    paid: 'Paid', open: 'Open', cancelled: 'Cancelled',
  };
  return labels[status] || status.charAt(0).toUpperCase() + status.slice(1);
}

function getAuditStatusColor(status) {
  switch (status) {
    case 'requested': return 'warning';
    case 'approved': return 'info';
    case 'ready': return 'primary';
    case 'memo': return 'warning';
    case 'paid': return 'success';
    case 'canceled': case 'cancelled': return 'default';
    case 'open': return 'primary';
    default: return 'default';
  }
}

/**
 * Reusable audit log dialog used by both CertifiedInventory and LotsInventory.
 * Wrapped in React.memo — only re-renders when props actually change.
 *
 * Props:
 *   open    – boolean
 *   onClose – () => void
 *   logs    – array of audit log objects
 *   loading – boolean
 *   title   – string label shown in the dialog title chip
 */
const AuditLogDialog = memo(({ open, onClose, logs, loading, title }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth fullScreen={isMobile}>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <HistoryIcon color="action" />
          <Typography variant="h6">Audit Log</Typography>
          {title && (
            <Chip label={title} size="small" variant="outlined" sx={{ ml: 1 }} />
          )}
        </Box>
      </DialogTitle>
      <DialogContent dividers sx={{ minHeight: 200 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 3 }}>
            <CircularProgress size={32} />
          </Box>
        ) : logs.length === 0 ? (
          <Typography color="text.secondary" sx={{ py: 3 }} textAlign="center">
            No audit logs found for this item.
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Action</TableCell>
                  <TableCell>Performed By</TableCell>
                  <TableCell>Client</TableCell>
                  <TableCell>Order</TableCell>
                  <TableCell>From</TableCell>
                  <TableCell>To</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {logs.map((log) => (
                  <TableRow key={log._id}>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <Typography variant="caption">
                        {log.createdAt ? formatDateTime(log.createdAt) : '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={formatAuditAction(log.action)}
                        size="small"
                        color={getAuditActionColor(log.action)}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      {log.performedBy
                        ? `${log.performedBy.firstName || ''} ${log.performedBy.lastName || ''}`.trim() || log.performedBy.email
                        : '—'}
                    </TableCell>
                    <TableCell><AuditLogClientLinkCell log={log} /></TableCell>
                    <TableCell><AuditLogOrderLinkCell log={log} /></TableCell>
                    <TableCell>
                      <AuditLogFromCell log={log} getAuditStatusLabel={getAuditStatusLabel} />
                    </TableCell>
                    <TableCell>
                      <AuditLogToCell log={log} getAuditStatusLabel={getAuditStatusLabel} getToChipColor={getAuditStatusColor} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
});

AuditLogDialog.displayName = 'AuditLogDialog';

export default AuditLogDialog;
